import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Plus, Trash2, CheckCircle2, Circle, Clock, Lock, Unlock,
  Sun, Sparkles, Moon, Heart, Calendar, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  morning: { label: 'Morning Routine', icon: Sun, bg: '#FDF0D5', accent: '#F0C040' },
  chores: { label: 'Chores', icon: Sparkles, bg: '#A2D2FF', accent: '#6BA3D6' },
  evening: { label: 'Evening Wind-down', icon: Moon, bg: '#CDB4DB', accent: '#A080B0' },
  self_care: { label: 'Self-Care', icon: Heart, bg: '#FFC8DD', accent: '#E090A0' },
  custom: { label: 'Custom', icon: Calendar, bg: '#B9FBC0', accent: '#70C080' },
};

const DAYS_OPTIONS = [
  { value: 'everyday', label: 'Every Day' },
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

export default function SchedulePage({ onBack }) {
  const [scheduleItems, setScheduleItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '', description: '', scheduled_time: '09:00',
    days: ['everyday'], is_blocking: true, category: 'custom', points: 15
  });

  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/schedule`, { withCredentials: true });
      setScheduleItems(data);
    } catch (e) {
      console.error('Failed to fetch schedule:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.title.trim()) return;
    try {
      await axios.post(`${API}/schedule`, newItem, { withCredentials: true });
      toast.success('Schedule item added!');
      setNewItem({ title: '', description: '', scheduled_time: '09:00', days: ['everyday'], is_blocking: true, category: 'custom', points: 15 });
      setShowAddForm(false);
      fetchSchedule();
    } catch (e) {
      toast.error('Failed to add item');
    }
  };

  const handleToggleComplete = async (item) => {
    try {
      await axios.patch(
        `${API}/schedule/${item.id}`,
        { completed_today: !item.completed_today },
        { withCredentials: true }
      );
      if (!item.completed_today) {
        toast.success(`+${item.points} points! Great job!`, { icon: '🎉' });
      }
      fetchSchedule();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/schedule/${itemId}`, { withCredentials: true });
      toast.success('Removed from schedule');
      fetchSchedule();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleApplyTemplate = async (templateName) => {
    try {
      const { data } = await axios.post(
        `${API}/schedule/apply-template/${templateName}`,
        {},
        { withCredentials: true }
      );
      toast.success(data.message);
      setShowTemplates(false);
      fetchSchedule();
    } catch (e) {
      toast.error('Failed to apply template');
    }
  };

  const handleDayToggle = (day) => {
    if (day === 'everyday') {
      setNewItem({ ...newItem, days: ['everyday'] });
      return;
    }
    let newDays = newItem.days.filter(d => d !== 'everyday');
    if (newDays.includes(day)) {
      newDays = newDays.filter(d => d !== day);
    } else {
      newDays.push(day);
    }
    if (newDays.length === 0) newDays = ['everyday'];
    setNewItem({ ...newItem, days: newDays });
  };

  // Group items by category
  const groupedItems = {};
  scheduleItems.forEach(item => {
    const cat = item.category || 'custom';
    if (!groupedItems[cat]) groupedItems[cat] = [];
    groupedItems[cat].push(item);
  });

  if (loading) {
    return (
      <div className="neo-card p-8 text-center">
        <div className="animate-pulse-soft">Loading schedule...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedule-page">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="neo-btn p-2 bg-white"
          data-testid="schedule-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-3xl font-bold font-['Fredoka']">Tidsschema</h2>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => { setShowAddForm(!showAddForm); setShowTemplates(false); }}
          className="neo-btn px-5 py-3 bg-[#B9FBC0] flex items-center gap-2"
          data-testid="add-schedule-btn"
        >
          <Plus className="w-5 h-5" />
          <span>Lägg till påminnelse</span>
        </button>
        <button
          onClick={() => { setShowTemplates(!showTemplates); setShowAddForm(false); }}
          className="neo-btn px-5 py-3 bg-[#CDB4DB] flex items-center gap-2"
          data-testid="templates-btn"
        >
          <Sparkles className="w-5 h-5" />
          <span>Använd mall</span>
        </button>
      </div>

      {/* Help banner */}
      <div className="neo-card p-3 bg-[#FFF7B5] text-xs flex items-start gap-2" data-testid="schedule-help-banner">
        <Lock className="w-4 h-4 mt-0.5 shrink-0 text-red-600" />
        <div>
          <strong>Blockerande påminnelse:</strong> När den röda låsknappen är PÅ — kommer hela appen låsa sig kl. den valda tiden tills du bockar av uppgiften. Perfekt för "lägg in tvätt 21:00" eller "ta medicin". Du kan inte missa det.
        </div>
      </div>

      {/* Templates Panel */}
      {showTemplates && (
        <div className="neo-card p-6 animate-bounce-in" data-testid="templates-panel">
          <h3 className="text-xl font-bold font-['Fredoka'] mb-4">Färdiga rutiner</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(CATEGORY_CONFIG).filter(([k]) => k !== 'custom').map(([key, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => handleApplyTemplate(key)}
                  className="neo-card p-4 flex items-center gap-4 text-left hover:scale-[1.02] transition-transform"
                  style={{ backgroundColor: config.bg }}
                  data-testid={`template-${key}`}
                >
                  <div className="w-12 h-12 rounded-full border-2 border-black flex items-center justify-center bg-white">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold font-['Fredoka'] text-lg">{config.label}</h4>
                    <p className="text-sm text-gray-600">Lägg till färdiga punkter</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Custom Item Form */}
      {showAddForm && (
        <div className="neo-card p-6 animate-bounce-in" data-testid="add-schedule-form">
          <h3 className="text-xl font-bold font-['Fredoka'] mb-4">Ny påminnelse</h3>
          <form onSubmit={handleAddItem} className="space-y-4">
            <div>
              <label className="block text-sm font-bold mb-1">Vad ska göras?</label>
              <input
                type="text"
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                className="neo-input w-full"
                placeholder="t.ex. Lägg in kläder i tvättmaskinen"
                required
                data-testid="schedule-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Beskrivning (valfritt)</label>
              <input
                type="text"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="neo-input w-full"
                placeholder="Extra detaljer..."
                data-testid="schedule-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-1">Klockslag</label>
                <input
                  type="time"
                  value={newItem.scheduled_time}
                  onChange={(e) => setNewItem({ ...newItem, scheduled_time: e.target.value })}
                  className="neo-input w-full"
                  data-testid="schedule-time-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Poäng</label>
                <select
                  value={newItem.points}
                  onChange={(e) => setNewItem({ ...newItem, points: parseInt(e.target.value) })}
                  className="neo-input w-full"
                  data-testid="schedule-points-select"
                >
                  <option value="5">5 pts</option>
                  <option value="10">10 pts</option>
                  <option value="15">15 pts</option>
                  <option value="20">20 pts</option>
                  <option value="30">30 pts</option>
                </select>
              </div>
            </div>

            {/* Days Selection */}
            <div>
              <label className="block text-sm font-bold mb-2">Upprepa på</label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OPTIONS.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => handleDayToggle(day.value)}
                    className={`neo-btn px-3 py-1.5 text-sm ${
                      newItem.days.includes(day.value) ? 'bg-[#A2D2FF]' : 'bg-white'
                    }`}
                    data-testid={`day-${day.value}`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Blocking Toggle */}
            <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-black/20 bg-white">
              <button
                type="button"
                onClick={() => setNewItem({ ...newItem, is_blocking: !newItem.is_blocking })}
                className={`w-12 h-7 rounded-full border-2 border-black flex items-center transition-all shrink-0 ${
                  newItem.is_blocking ? 'bg-[#FF6B6B] justify-end' : 'bg-gray-200 justify-start'
                }`}
                data-testid="blocking-toggle"
              >
                <div className="w-5 h-5 rounded-full bg-white border-2 border-black mx-0.5" />
              </button>
              <div className="flex items-center gap-2 flex-1">
                {newItem.is_blocking ? (
                  <Lock className="w-4 h-4 text-red-500 shrink-0" />
                ) : (
                  <Unlock className="w-4 h-4 text-gray-400 shrink-0" />
                )}
                <div>
                  <p className="font-semibold text-sm leading-tight">
                    {newItem.is_blocking ? '🔒 Låser appen tills den är klar' : 'Mjuk påminnelse (utan lås)'}
                  </p>
                  <p className="text-[11px] opacity-70 leading-snug mt-0.5">
                    {newItem.is_blocking
                      ? 'Du kommer få en kawaii-fullskärm-popup vid valt klockslag som du MÅSTE bocka av'
                      : 'Bara en notis — du kan ignorera den'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="submit" className="neo-btn flex-1 py-3 bg-[#B9FBC0]" data-testid="save-schedule-btn">
                Spara påminnelse
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="neo-btn px-4 py-3 bg-gray-200"
                data-testid="cancel-schedule-btn"
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Schedule Items by Category */}
      {scheduleItems.length === 0 ? (
        <div className="neo-card p-8 text-center">
          <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-bold font-['Fredoka'] mb-2">No schedule yet!</h3>
          <p className="text-gray-600">Add items or use a template to build your daily routine.</p>
        </div>
      ) : (
        Object.entries(groupedItems).map(([category, items]) => {
          const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.custom;
          const Icon = config.icon;
          
          return (
            <div key={category} className="space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-full border-2 border-black flex items-center justify-center"
                  style={{ backgroundColor: config.bg }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <h3 className="text-xl font-bold font-['Fredoka']">{config.label}</h3>
              </div>

              {items.map((item) => (
                <div
                  key={item.id}
                  className={`task-item flex items-center gap-4 ${item.completed_today ? 'completed' : ''}`}
                  data-testid={`schedule-item-${item.id}`}
                >
                  {/* Complete toggle */}
                  <button
                    onClick={() => handleToggleComplete(item)}
                    className={`w-8 h-8 rounded-full border-2 border-black flex items-center justify-center transition-all ${
                      item.completed_today ? 'bg-[#B9FBC0]' : 'bg-white hover:bg-gray-100'
                    }`}
                    data-testid={`schedule-toggle-${item.id}`}
                  >
                    {item.completed_today && <CheckCircle2 className="w-5 h-5" />}
                  </button>

                  {/* Time badge */}
                  <div className="flex items-center gap-1 px-2 py-1 bg-white rounded-lg border border-black min-w-[4rem] justify-center">
                    <Clock className="w-3 h-3" />
                    <span className="text-sm font-bold">{item.scheduled_time}</span>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <p className={`font-semibold task-title ${item.completed_today ? 'line-through text-gray-500' : ''}`}>
                      {item.title}
                    </p>
                    {item.description && (
                      <p className="text-sm text-gray-500">{item.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {item.is_blocking && (
                        <span className="text-xs bg-[#FFC8DD] px-2 py-0.5 rounded-full border border-black flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Blocking
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {item.days.includes('everyday') ? 'Every day' : item.days.join(', ')}
                      </span>
                    </div>
                  </div>

                  {/* Points & Delete */}
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-[#FDF0D5] rounded-lg text-xs font-bold border border-black">
                      +{item.points}
                    </span>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="w-8 h-8 rounded-lg border-2 border-black bg-white hover:bg-red-100 flex items-center justify-center"
                      data-testid={`schedule-delete-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}
